import { motion, AnimatePresence } from "framer-motion";
import { X, Linkedin, Twitter, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { resolveImageUrl } from "../admin/ImageUpload";

/**
 * LeaderModal — accessible modal that shows a leader's full bio + social links.
 * Used by both Board of Directors and Advisory Committee cards.
 */
export default function LeaderModal({ leader, onClose }) {
  useEffect(() => {
    if (!leader) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [leader, onClose]);

  return (
    <AnimatePresence>
      {leader && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid="leader-modal-backdrop"
          className="fixed inset-0 z-[100] bg-[#0F172A]/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Profile of ${leader.name}`}
            data-testid="leader-modal"
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#0B1B40] border border-blue-100 dark:border-blue-800 shadow-[0_40px_120px_-40px_rgba(37,99,235,0.55)]"
          >
            <button
              onClick={onClose}
              aria-label="Close profile"
              data-testid="leader-modal-close"
              className="absolute top-4 right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 dark:bg-[#0F172A]/90 border border-blue-100 dark:border-blue-800 text-[#0F172A] dark:text-blue-100 hover:bg-blue-50 dark:hover:bg-blue-900"
            >
              <X size={16} />
            </button>

            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-2 relative bg-gradient-to-b from-blue-50 to-white dark:from-blue-950 dark:to-[#0B1B40]">
                <div className="aspect-[4/5] md:aspect-auto md:h-full overflow-hidden">
                  <img
                    src={resolveImageUrl(leader.photo_url)}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="md:col-span-3 p-7 md:p-9">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#2563EB] dark:text-cyan-300">
                  {leader.designation}
                </div>
                <h3 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#0F172A] dark:text-white leading-tight">
                  {leader.name}
                </h3>

                <div className="mt-6 text-[#475569] dark:text-blue-100 text-[15px] leading-relaxed whitespace-pre-line">
                  {leader.bio || "Full biography will be added shortly."}
                </div>

                {(leader.linkedin || leader.twitter) && (
                  <div className="mt-8 pt-6 border-t border-blue-100 dark:border-blue-800">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#475569] dark:text-blue-200 mb-3">
                      Connect
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {leader.linkedin && (
                        <a
                          href={leader.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="leader-modal-linkedin"
                          className="inline-flex items-center gap-2 rounded-full bg-[#0A66C2] hover:bg-[#0851A0] text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider"
                        >
                          <Linkedin size={13} /> LinkedIn <ExternalLink size={11} />
                        </a>
                      )}
                      {leader.twitter && (
                        <a
                          href={leader.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="leader-modal-twitter"
                          className="inline-flex items-center gap-2 rounded-full bg-black hover:bg-[#1D1D1F] text-white px-5 py-2 text-xs font-semibold uppercase tracking-wider"
                        >
                          <Twitter size={13} /> Twitter/X <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
